import { useMemo } from "react";
import PropTypes from "prop-types";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function PlatformRevenueChart({ stats, previousWeekStats, niveauGlobal }) {
  console.log('📊 PlatformRevenueChart - Stats cette semaine:', stats);
  console.log('📊 PlatformRevenueChart - Stats semaine précédente:', previousWeekStats);
  console.log('📊 PlatformRevenueChart - Niveau global:', niveauGlobal);

  // Extraire les valeurs numériques des stats
  const extractValue = (statString) => {
    if (!statString) return 0;
    // Enlever le symbole € et remplacer la virgule par un point
    const cleaned = statString.replace(/[€\s]/g, '').replace(',', '.');
    const value = parseFloat(cleaned);
    return isNaN(value) ? 0 : value;
  };

  // Préparer les données pour le graphique avec comparaison
  const chartData = useMemo(() => {
    // Données de la semaine actuelle
    const uberCurrent = extractValue(stats?.uber?.count);
    const boltCurrent = extractValue(stats?.bolt?.count);
    const heetchCurrent = extractValue(stats?.heetch?.count);
    const freenowCurrent = extractValue(stats?.freenow?.count);
    const especeCurrent = extractValue(stats?.espece?.count);

    // Données de la semaine précédente
    const uberPrevious = previousWeekStats?.uber || 0;
    const boltPrevious = previousWeekStats?.bolt || 0;
    const heetchPrevious = previousWeekStats?.heetch || 0;
    const freenowPrevious = previousWeekStats?.freenow || 0;
    const especePrevious = previousWeekStats?.espece || 0;

    const totalCurrent = uberCurrent + boltCurrent + heetchCurrent + freenowCurrent + especeCurrent;
    const totalPrevious = uberPrevious + boltPrevious + heetchPrevious + freenowPrevious + especePrevious;

    return {
      labels: ["UBER", "BOLT", "HEETCH", "FREENOW", "ESPÈCES"],
      datasets: [
        {
          label: "Cette semaine",
          data: [uberCurrent, boltCurrent, heetchCurrent, freenowCurrent, especeCurrent],
          color: "dark",
        },
        {
          label: "Semaine précédente",
          data: [uberPrevious, boltPrevious, heetchPrevious, freenowPrevious, especePrevious],
          color: "info",
        },
      ],
      totalCurrent,
      totalPrevious,
      currentData: [uberCurrent, boltCurrent, heetchCurrent, freenowCurrent, especeCurrent],
      previousData: [uberPrevious, boltPrevious, heetchPrevious, freenowPrevious, especePrevious],
    };
  }, [stats, previousWeekStats]);

  // Calculer l'évolution en pourcentage
  const calculateEvolution = (current, previous) => {
    if (previous === 0) {
      if (current === 0) return { value: 0, text: "0%", color: "text" };
      return { value: 100, text: "+100%", color: "success" };
    }
    const evolution = ((current - previous) / previous) * 100;
    const sign = evolution >= 0 ? "+" : "";
    const color = evolution >= 0 ? "success" : "error";
    return { value: evolution, text: `${sign}${evolution.toFixed(1)}%`, color };
  };

  const platformData = [
    {
      name: "UBER",
      currentValue: chartData.currentData[0],
      previousValue: chartData.previousData[0],
      evolution: calculateEvolution(chartData.currentData[0], chartData.previousData[0]),
      color: "dark",
      icon: "local_taxi"
    },
    {
      name: "BOLT",
      currentValue: chartData.currentData[1],
      previousValue: chartData.previousData[1],
      evolution: calculateEvolution(chartData.currentData[1], chartData.previousData[1]),
      color: "success",
      icon: "electric_bolt"
    },
    {
      name: "HEETCH",
      currentValue: chartData.currentData[2],
      previousValue: chartData.previousData[2],
      evolution: calculateEvolution(chartData.currentData[2], chartData.previousData[2]),
      color: "primary",
      icon: "directions_car"
    },
    {
      name: "FREENOW",
      currentValue: chartData.currentData[3],
      previousValue: chartData.previousData[3],
      evolution: calculateEvolution(chartData.currentData[3], chartData.previousData[3]),
      color: "warning",
      icon: "directions"
    },
    {
      name: "ESPÈCES",
      currentValue: chartData.currentData[4],
      previousValue: chartData.previousData[4],
      evolution: calculateEvolution(chartData.currentData[4], chartData.previousData[4]),
      color: "info",
      icon: "euro"
    },
  ];

  // Configuration du graphique Chart.js
  const chartConfig = useMemo(() => {
    return {
      data: {
        labels: chartData.labels,
        datasets: [
          {
            label: "Cette semaine",
            data: chartData.currentData,
            backgroundColor: "rgba(52, 71, 103, 0.8)",
            borderColor: "rgba(52, 71, 103, 1)",
            borderWidth: 1,
            borderRadius: 8,
            maxBarThickness: 40,
          },
          {
            label: "Semaine précédente",
            data: chartData.previousData,
            backgroundColor: "rgba(26, 115, 232, 0.8)",
            borderColor: "rgba(26, 115, 232, 1)",
            borderWidth: 1,
            borderRadius: 8,
            maxBarThickness: 40,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "top",
            labels: {
              color: "#fff",
              font: {
                size: 12,
                family: "Roboto",
              },
              padding: 15,
            },
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            padding: 12,
            titleColor: "#fff",
            bodyColor: "#fff",
            borderColor: "rgba(255, 255, 255, 0.2)",
            borderWidth: 1,
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                label += context.parsed.y.toFixed(2).replace(".", ",") + ' €';
                return label;
              }
            }
          },
        },
        interaction: {
          intersect: false,
          mode: "index",
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              drawBorder: false,
              display: true,
              drawOnChartArea: true,
              drawTicks: false,
              borderDash: [5, 5],
              color: "rgba(255, 255, 255, .2)",
            },
            ticks: {
              padding: 10,
              font: {
                size: 12,
                weight: 300,
                family: "Roboto",
              },
              color: "#fff",
              callback: function(value) {
                return value.toFixed(0) + ' €';
              }
            },
          },
          x: {
            grid: {
              drawBorder: false,
              display: false,
              drawOnChartArea: false,
              drawTicks: false,
            },
            ticks: {
              display: true,
              color: "#fff",
              padding: 10,
              font: {
                size: 11,
                weight: 300,
                family: "Roboto",
              },
            },
          },
        },
      },
    };
  }, [chartData]);

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox padding="1rem">
        {/* Graphique */}
        <MDBox
          variant="gradient"
          bgColor="dark"
          borderRadius="lg"
          coloredShadow="dark"
          py={2}
          px={2}
          mt={-5}
          height="18rem"
        >
          <Bar data={chartConfig.data} options={chartConfig.options} />
        </MDBox>

        {/* Détails par plateforme */}
        <MDBox pt={3} pb={1} px={1}>
          <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
            Comparaison hebdomadaire
          </MDTypography>
          <MDBox display="flex" justifyContent="space-between" mb={1}>
            <MDTypography variant="button" color="text" fontWeight="regular">
              Total cette semaine:
            </MDTypography>
            <MDTypography variant="button" fontWeight="bold">
              {chartData.totalCurrent.toFixed(2).replace(".", ",")} €
            </MDTypography>
          </MDBox>
          <MDBox display="flex" justifyContent="space-between" mb={2}>
            <MDTypography variant="button" color="text" fontWeight="regular">
              Total semaine précédente:
            </MDTypography>
            <MDTypography variant="button" fontWeight="bold">
              {chartData.totalPrevious.toFixed(2).replace(".", ",")} €
            </MDTypography>
          </MDBox>
          <Divider />
          <MDBox>
            {platformData.map((platform, index) => (
              <MDBox key={platform.name} py={1.5} pr={2}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <MDTypography variant="button" fontWeight="bold" textTransform="capitalize">
                    {platform.name}
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="bold" color={platform.evolution.color}>
                    {platform.evolution.text}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography variant="caption" color="text">
                    Cette semaine: {platform.currentValue.toFixed(2).replace(".", ",")} €
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    Précédente: {platform.previousValue.toFixed(2).replace(".", ",")} €
                  </MDTypography>
                </MDBox>
                {index < platformData.length - 1 && <Divider sx={{ mt: 1 }} />}
              </MDBox>
            ))}
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

PlatformRevenueChart.propTypes = {
  stats: PropTypes.shape({
    uber: PropTypes.shape({
      count: PropTypes.string,
      percentage: PropTypes.string,
      label: PropTypes.string,
    }),
    bolt: PropTypes.shape({
      count: PropTypes.string,
      percentage: PropTypes.string,
      label: PropTypes.string,
    }),
    heetch: PropTypes.shape({
      count: PropTypes.string,
      percentage: PropTypes.string,
      label: PropTypes.string,
    }),
    freenow: PropTypes.shape({
      count: PropTypes.string,
      percentage: PropTypes.string,
      label: PropTypes.string,
    }),
    espece: PropTypes.shape({
      count: PropTypes.string,
      percentage: PropTypes.string,
      label: PropTypes.string,
    }),
  }),
  previousWeekStats: PropTypes.shape({
    uber: PropTypes.number,
    bolt: PropTypes.number,
    heetch: PropTypes.number,
    freenow: PropTypes.number,
    espece: PropTypes.number,
  }),
  niveauGlobal: PropTypes.number,
};

PlatformRevenueChart.defaultProps = {
  stats: null,
  previousWeekStats: null,
  niveauGlobal: 0,
};

export default PlatformRevenueChart;
